import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme, keyframes } from "styled-components";
import { ReactComponent as Unfold } from "../../Assets/icons/Unfold_More.svg";
import { useAuthService } from "../../Context/AuthContext";
import { useCallsService } from "../../Context/CallsContext";
import { translateMessageAPICall } from "../../API/translate/translateMessage";
import { useRealtimeTranscription } from "../../hooks/useRealtimeTranscription";
import LanguageListSelector from "../Auth Components/LanguageListSelector";
import { useTranslate } from '@tolgee/react';
import DeleteConfirmation from "../GenericComponents/DeleteConfirmations";
import { useNotify } from "../../Helpers/Notifications";
import { Copy, Mic, Square, Volume2, Loader2 } from "lucide-react";
import { downloadFile } from "../../Helpers/downloadHelper";

const TranslationItem = ({ item, onPlay, onCopy, activeAudioText, loadingAudioText }) => {
  const { t } = useTranslate();
  const { notifySuccess } = useNotify();
  
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    notifySuccess(t("translatePage.copied"));
  };

  const isSourceLoading = loadingAudioText === item.initialText;
  const isSourcePlaying = activeAudioText === item.initialText;
  
  const isResultLoading = loadingAudioText === item.translatedText;
  const isResultPlaying = activeAudioText === item.translatedText;

  return (
    <BubbleContainer>
      <SourceSection>
        <TextRow>
          <LanguageTag>{item.fromLanguage}</LanguageTag>
          <ActionGroup>
            <ActionBtn onClick={() => handleCopy(item.initialText)}>
              <Copy size={20} />
            </ActionBtn>
            <ActionBtn $active={isSourcePlaying} onClick={() => onPlay(item.initialText)}>
              {isSourceLoading ? (
                <SpinningIconWrapper><Loader2 size={26} /></SpinningIconWrapper>
              ) : (
                <Volume2 size={26} />
              )}
            </ActionBtn>
          </ActionGroup>
        </TextRow>
        <OriginalText>{item.initialText}</OriginalText>
      </SourceSection>

      <Divider />

      <ResultSection>
        <TextRow>
          <LanguageTag>{item.toLanguage}</LanguageTag>
          <ActionGroup>
            <ActionBtn onClick={() => handleCopy(item.translatedText)}>
              <Copy size={20} />
            </ActionBtn>
            <ActionBtn $active={isResultPlaying} onClick={() => onPlay(item.translatedText)}>
              {isResultLoading ? (
                <SpinningIconWrapper><Loader2 size={26} /></SpinningIconWrapper>
              ) : (
                <Volume2 size={26} />
              )}
            </ActionBtn>
          </ActionGroup>
        </TextRow>
        <TranslatedText>{item.translatedText}</TranslatedText>
      </ResultSection>
    </BubbleContainer>
  );
};

const TranslateComponent = ({
  translationHistory,
  setTranslationHistory,
  fromLanguage,
  setFromLanguage,
  toLanguage,
  setToLanguage,
  from,
  setFrom,
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const { notifySuccess, notifyError } = useNotify();
  
  const {
    translationsLanguages,
    userRef,
    getTranslationsLanguages,
    getCallTranslationsLanguages,
  } = useAuthService();
  const { speakAudio } = useCallsService();

  const fromRef = useRef();
  const audioFilesRef = useRef();
  const addToHistoryRef = useRef();
  
  const currentAudioRef = useRef(null);
  const [activeAudioText, setActiveAudioText] = useState(null);
  const [loadingAudioText, setLoadingAudioText] = useState(null);

  const [to, setTo] = useState("");
  const [fromLanguageOpen, setFromLanguageOpen] = useState(false);
  const [toLanguageOpen, setToLanguageOpen] = useState(false);

  const fromLanguageObj = translationsLanguages?.find((l) => l.name === fromLanguage) || null;

  const handleFinalTranscript = (text) => {
    if (!text) return;
    addToHistoryRef.current = true;
    setFrom((prev) => (prev && prev.trim().length > 0 ? `${prev.trim()} ${text}` : text));
  };

  const {
    isListening: recordinOn,
    startListening,
    stopListening,
    error: transcriptionError,
  } = useRealtimeTranscription({
    language: fromLanguageObj?.locale || fromLanguage,
    onFinalText: handleFinalTranscript,
  });

  useEffect(() => {
    if (transcriptionError) {
      notifyError(transcriptionError);
    }
  }, [transcriptionError]);

  const confirmClearSession = () => {
    setTranslationHistory([]);
    setFrom("");
    setTo("");
    notifySuccess(t("translatePage.cleared")); 
  };

  const handleExport = () => {
    if (translationHistory.length === 0) {
      notifyError(t("translatePage.nothing_to_export"));
      return;
    }

    const exportHeader = t("translatePage.export_header");
    const messageLabel = t("translatePage.export_message");
    const rawName = t("translatePage.export_filename");
    const fileName = `${rawName}_${new Date().toISOString().slice(0, 10)}.txt`;

    let content = `${exportHeader}\n\n`;
    const chronologicalHistory = [...translationHistory].reverse();

    chronologicalHistory.forEach((item, index) => {
      content += `[${messageLabel} ${index + 1}]\n`;
      content += `${item.fromLanguage}: ${item.initialText}\n`;
      content += `${item.toLanguage}: ${item.translatedText}\n`;
      content += `---------------------------\n\n`;
    });

    downloadFile(content, fileName, { isText: true });
    notifySuccess(t("live_feed.download_success"));
  };

  const handleSwapLanguages = () => {
    const tempLang = fromLanguage;
    setFromLanguage(toLanguage);
    setToLanguage(tempLang);
    
    const tempText = from;
    setFrom(to);
    setTo(tempText);
  };

  const handleTranslate = (initialLanguage, translateTo, message) => {
    translateMessageAPICall(
      initialLanguage,
      translateTo,
      message,
      (response) => {
        let translatedText = response.data.translation.response;
        if (translatedText && translatedText.includes("[debug]")) {
          translatedText = translatedText.split("[debug]")[0].trim();
        }
        setTo(translatedText);
      },
      (error) => console.log(error)
    );
  };

  const handleInputChange = (e) => {
    setFrom(e.target.value);
    
    if (fromRef.current) {
      fromRef.current.style.height = "auto";
      fromRef.current.style.height = `${fromRef.current.scrollHeight}px`;
    }
  };

  const handleAddItemToTranslationHistory = (e) => {
    if (e.code === "Enter" && !e.shiftKey) {
      e.preventDefault();
      
      if (!from || from.trim().length === 0) {
        return;
      }

      if (!to || to.trim().length === 0) {
        notifyError(t('translatePage.wait_translation'));
        return;
      }

      let newObject = {
        fromLanguage: fromLanguage,
        toLanguage: toLanguage,
        initialText: from.trimStart(),
        translatedText: to,
      };

      setTranslationHistory((prev) => [newObject, ...prev]);
      setFrom("");
      setTo("");
    }
  };

  const handlePlay = (audioFile, text) => {
    if (!audioFile) return;
    
    const audio = new Audio(audioFile);
    currentAudioRef.current = audio;
    audio.play();
    
    audio.onended = () => {
      currentAudioRef.current = null;
      setActiveAudioText(null);
      audioFilesRef.current = null;
    };
  };

  const handleSpeakClick = (text) => {
    if (currentAudioRef.current && activeAudioText === text) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setActiveAudioText(null);
      return;
    }

    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    setLoadingAudioText(text);

    speakAudio(
      text,
      userRef.current.call_language,
      (response) => {
        setLoadingAudioText(null);
        setActiveAudioText(text);
        const url = URL.createObjectURL(response.data);
        audioFilesRef.current = url;
        handlePlay(audioFilesRef.current, text);
      },
      (error) => {
        setLoadingAudioText(null);
        console.log(error);
        setActiveAudioText(null);
      }
    );
  };

  useEffect(() => {
    if (!addToHistoryRef.current) {
      let time = setTimeout(() => {
        if ((from === "" || from.trim().length === 0) && !recordinOn) {
          handleTranslate(fromLanguage, toLanguage, "");
          setTo("");
          return;
        }
        handleTranslate(fromLanguage, toLanguage, from.trimStart());
      }, 250);

      return () => clearInterval(time);
    } else {
      handleTranslate(fromLanguage, toLanguage, from.trimStart());
      addToHistoryRef.current = false;
    }
  }, [fromLanguage, toLanguage, from]);

  useEffect(() => {
    if (translationsLanguages.length !== 0) return;
    getTranslationsLanguages(() => {}, () => {});
    getCallTranslationsLanguages(() => {}, () => {});
  }, []);

  useEffect(() => {
    if (from === "" && fromRef.current) {
      fromRef.current.style.height = "auto";
    }
  }, [from]);

  const isLiveResultLoading = loadingAudioText === to;
  const isLiveResultPlaying = activeAudioText === to;

  return (
    <Wrapper>
      <>
        {fromLanguageOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 999 }}>
            <LanguageListSelector
              isLanguageSelectorOpen={fromLanguageOpen}
              openLanguageList={() => setFromLanguageOpen(false)}
              valuesList={translationsLanguages}
              setValue={setFromLanguage}
            />
          </div>
        )}
        {toLanguageOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 999 }}>
            <LanguageListSelector
              isLanguageSelectorOpen={toLanguageOpen}
              openLanguageList={() => setToLanguageOpen(false)}
              valuesList={translationsLanguages}
              setValue={setToLanguage}
            />
          </div>
        )}
      </>

      <Header>
        <HeaderActions>
          <DeleteConfirmation 
            handleRemoveItem={confirmClearSession}
            customMessage={t('translatePage.confirm_clear')}
          />
          
          <IconButton onClick={handleExport} title={t("translatePage.export_conversation")}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                <polyline points="16 6 12 2 8 6"></polyline>
                <line x1="12" y1="2" x2="12" y2="15"></line>
              </svg>
          </IconButton>
        </HeaderActions>
      </Header>

      <ContentContainer>
        
        <FeedArea>
          {translationHistory.map((item, index) => (
            <TranslationItem
              key={index}
              item={item}
              onPlay={handleSpeakClick}
              activeAudioText={activeAudioText}
              loadingAudioText={loadingAudioText}
            />
          ))}
        </FeedArea>

        <BottomAnchor>
          <CapsuleWrapper>
            <LanguageCapsule>
              <LangSelectBtn onClick={() => setFromLanguageOpen(true)}>
                {fromLanguage || t('translatePage.default_from_lang')} <Unfold />
              </LangSelectBtn>
              <SwapBtn onClick={handleSwapLanguages}>
                <span>⇄</span>
              </SwapBtn>
              <LangSelectBtn onClick={() => setToLanguageOpen(true)}>
                {toLanguage || t('translatePage.default_to_lang')} <Unfold />
              </LangSelectBtn>
            </LanguageCapsule>
          </CapsuleWrapper>

          <ActiveInputContainer>
            <InputWrapper>
              <LanguageTag>{fromLanguage}</LanguageTag>
              <LanguageContentInput
                ref={fromRef}
                placeholder={t('translatePage.enter_text')}
                value={from}
                onChange={handleInputChange}
                onKeyDown={(e) => handleAddItemToTranslationHistory(e)}
              />
              <MicContainer>
                {recordinOn ? (
                  <MicButton $active onClick={stopListening}>
                    <Square size={16} fill="currentColor" />
                  </MicButton>
                ) : (
                  <MicButton onClick={startListening}>
                    <Mic size={20} />
                  </MicButton>
                )}
              </MicContainer>
            </InputWrapper>

            {(from || to) && (
              <LivePreview>
                <LiveTextRow>
                  <LanguageTag>{toLanguage}</LanguageTag>
                  <ActionGroup>
                    <ActionBtn onClick={() => {
                      navigator.clipboard.writeText(to);
                      notifySuccess(t("translatePage.copied"));
                    }}>
                      <Copy size={20} />
                    </ActionBtn>
                    <ActionBtn $active={isLiveResultPlaying} onClick={() => handleSpeakClick(to)}>
                      {isLiveResultLoading ? (
                        <SpinningIconWrapper><Loader2 size={26} /></SpinningIconWrapper>
                      ) : (
                        <Volume2 size={26} />
                      )}
                    </ActionBtn>
                  </ActionGroup>
                </LiveTextRow>
                <LiveTranslatedText>
                  {to ? (
                    to
                  ) : (
                    <TypingDots>
                      <span />
                      <span />
                      <span />
                    </TypingDots>
                  )}
                </LiveTranslatedText>
              </LivePreview>
            )}
          </ActiveInputContainer>
        </BottomAnchor>

      </ContentContainer>
    </Wrapper>
  );
};

export default TranslateComponent;


const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(178, 175, 255, 0.4); }
  70% { box-shadow: 0 0 0 12px rgba(178, 175, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(178, 175, 255, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${spin} 1s linear infinite;
  width: 26px;
  height: 26px;
  color: ${({ theme }) => theme.menu.selectedTextColor};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center; 
  width: 100%;
  height: 100vh;
  background: ${({ theme }) => theme.menu.background};
  position: relative; 
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end; 
  padding: 17px 24px;
  background: rgba(28, 28, 35, 0.85); 
  backdrop-filter: blur(16px); 
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08); 
  position: relative; 
  z-index: 10; 
  width: 100%;
  flex-shrink: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  border-radius: 8px;
  transition: background 0.2s;
  
  svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const DeleteButtonWrapper = styled.div`
  ${IconButton} {
    svg {
      fill: currentColor; 
      stroke: none;
    }
    &:hover {
      color: #FF5252; 
      background: rgba(255, 82, 82, 0.1); 
      transform: scale(1.05); 
    }
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  max-width: 850px; 
  flex: 1; 
  position: relative;
  overflow: hidden; 
`;

const FeedArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 24px 20px 24px;
  display: flex;
  flex-direction: column-reverse;
  gap: 20px;
`;

const BottomAnchor = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  z-index: 10;
  padding-bottom: 20px;
`;

const CapsuleWrapper = styled.div`
  padding: 0 24px 24px 24px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const LanguageCapsule = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(10, 10, 15, 0.3); 
  border-radius: 40px;
  padding: 8px;
  width: 100%;
  max-width: 500px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0px 4px 12px rgba(0,0,0,0.1);
`;

const LangSelectBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center; 
  gap: 10px;
  background: rgba(255, 255, 255, 0.08); 
  border: none;
  color: #ffffff; 
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 24px;
  border-radius: 30px;
  transition: background 0.2s;
  flex: 1; 
  max-width: 180px;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12); 
  }
`;

const SwapBtn = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  transition: color 0.2s, transform 0.2s;

  &:hover {
    color: ${({ theme }) => theme.menu.primaryTextColor};
    transform: scale(1.1);
  }
`;

const ActiveInputContainer = styled.div`
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 16px;
  margin: 0 24px;
  padding: 16px 20px; 
  display: flex;
  flex-direction: column;
  gap: 12px; 
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.12); 
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px; 
  position: relative;
`;

const LanguageContentInput = styled.textarea`
  min-height: 40px;
  max-height: 200px;
  resize: none;
  background: transparent;
  border: none;
  outline: none;
  font-size: 16px; 
  font-weight: 400;
  line-height: 1.4;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  padding-right: 50px; 
  margin: 0;
  overflow-y: auto; 
  
  &::placeholder {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    opacity: 0.5;
  }
`;

const LiveTranslatedText = styled.div`
  font-size: 18px; 
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  margin: 0;
  height: auto;
  max-height: 150px; 
  overflow-y: auto; 
  white-space: pre-wrap; 
  word-wrap: break-word;
`;

const MicContainer = styled.div`
  position: absolute;
  bottom: -2px; 
  right: -4px;
`;

const MicButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px; 
  height: 38px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${({ $active, theme }) => 
    $active ? theme.menu.selectedTextColor : 'rgba(255, 255, 255, 0.06)'};
  
  color: ${({ $active }) => ($active ? '#fff' : 'inherit')};
  
  animation: ${({ $active }) => ($active ? pulseAnimation : 'none')} 1.5s infinite;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ $active, theme }) => 
      $active ? theme.menu.selectedTextColor : 'rgba(255, 255, 255, 0.12)'};
    transform: ${({ $active }) => ($active ? 'scale(1)' : 'scale(1.03)')};
  }
`;

const LivePreview = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px; 
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const LanguageTag = styled.span`
  font-size: 11px; 
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #ffffff; 
  opacity: 0.7; 
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  transition: opacity 0.2s ease;
`;

const BubbleContainer = styled.div`
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border: 1px solid rgba(255, 255, 255, 0.03);

  ${ActionGroup} {
    opacity: 0.2;
  }

  &:hover ${ActionGroup} {
    opacity: 1;
  }
`;

const LiveTextRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px; 
`;

const TextRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ActionBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme, $active }) => $active ? theme.menu.selectedTextColor : theme.menu.secondaryTextColor};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    color: ${({ theme }) => theme.menu.selectedTextColor}; 
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ResultSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const SourceSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TranslatedText = styled.p`
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.selectedTextColor}; 
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap; 
  word-wrap: break-word;
`;

const OriginalText = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap; 
  word-wrap: break-word;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: rgba(255, 255, 255, 0.05); 
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const TypingDots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px; 

  span {
    width: 6px;
    height: 6px;
    background-color: ${({ theme }) => theme.menu.selectedTextColor}; 
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out both;
  }

  span:nth-child(1) { animation-delay: -0.32s; }
  span:nth-child(2) { animation-delay: -0.16s; }
  span:nth-child(3) { animation-delay: 0s; }
`;